import { useMemo } from 'react';
import { Select } from './Select';
import { normalizeDialCode, phoneDigits } from '@/lib/phone';
import type { CountryCodeOption } from '@/services/patients.service';
import type { SelectOption } from '@/types/common.types';

/**
 * One option per dial code (SCRUM-1833).
 *
 * Several countries share a code (+1, +44, +7, +39), and the phone's country
 * is the code itself, so the list is the `/country-codes` rows grouped by
 * `phoneCode`. Each code appears once, is found by any country name that uses
 * it ("jersey" finds +44), and closes to just `+44` beside the number.
 *
 * A code not in the list — a stored '1242' — becomes its own option, so the
 * field never shows blank for a value it holds.
 */
export interface DialCodeOption extends SelectOption<string> {
  /** The countries shown beside the code, earliest countryID first. */
  countryNames: string[];
}

/** +1 is shared by the whole North American plan; it reads as just these two. */
const PLUS_ONE_NAMES = ['United States', 'Canada'];

export function buildDialCodeOptions(
  countries: CountryCodeOption[],
  currentCode?: string | null,
): DialCodeOption[] {
  const groups = new Map<string, CountryCodeOption[]>();
  for (const country of countries) {
    const code = phoneDigits(country.phoneCode);
    if (!code) continue;
    const members = groups.get(code) ?? [];
    members.push(country);
    groups.set(code, members);
  }

  const options: DialCodeOption[] = [...groups.entries()]
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([code, members]) => {
      const sorted = [...members].sort((a, b) => a.countryID - b.countryID);
      const countryNames = code === '1' ? PLUS_ONE_NAMES : sorted.map((c) => c.countryName);
      return {
        value: code,
        label: `+${code}`,
        countryNames,
        searchText: [
          `+${code}`,
          ...sorted.map((c) => `${c.countryName} ${c.countryAbbr ?? ''}`.trim()),
        ].join(' '),
      };
    });

  const current = normalizeDialCode(currentCode);
  if (!options.some((option) => option.value === current)) {
    options.unshift({ value: current, label: `+${current}`, countryNames: [], searchText: `+${current}` });
  }
  return options;
}

export interface DialCodeSelectProps {
  /** The dial code, e.g. '44'. Blank, missing or '0' shows as +1. */
  value: string | null | undefined;
  onChange: (code: string) => void;
  /** The `/country-codes` list (patientsSvc.listCountryCodes). */
  countries: CountryCodeOption[];
  loading?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  wrapperClassName?: string;
}

export function DialCodeSelect({
  value,
  onChange,
  countries,
  loading,
  disabled,
  id,
  className,
  wrapperClassName,
}: DialCodeSelectProps) {
  const current = normalizeDialCode(value);
  const options = useMemo(() => buildDialCodeOptions(countries, current), [countries, current]);

  return (
    <Select<string>
      id={id}
      options={options}
      value={current}
      searchable
      loading={loading}
      disabled={disabled}
      placeholder="+1"
      onChange={(next) => onChange(normalizeDialCode(next))}
      renderOption={(option) => {
        const names = (option as DialCodeOption).countryNames ?? [];
        return (
          <span>
            <span className="font-medium">{option.label}</span>
            {names.length > 0 && <span className="ml-2 text-slate-500">{names.join(', ')}</span>}
          </span>
        );
      }}
      className={className}
      wrapperClassName={wrapperClassName}
    />
  );
}
